import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlatformAuthProvider, usePlatformAuth } from "@/context/PlatformAuthContext";
import AppShell from "@/components/AppShell";
import PlatformShell from "@/components/PlatformShell";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import TallyConnect from "@/pages/TallyConnect";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import AcceptInvite from "@/pages/AcceptInvite";
import Settings from "@/pages/Settings";
import ChartOfAccounts from "@/pages/books/ChartOfAccounts";
import LedgerPage from "@/pages/books/LedgerPage";
import Parties from "@/pages/books/Parties";
import Vouchers from "@/pages/books/Vouchers";
import VoucherView from "@/pages/books/VoucherView";
import { Items, ItemView } from "@/pages/books/Items";
import Reports from "@/pages/books/Reports";
import ReportView from "@/pages/books/ReportView";
import PlatformLogin from "@/pages/platform/PlatformLogin";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import Organizations from "@/pages/platform/Organizations";
import OrganizationDetail from "@/pages/platform/OrganizationDetail";
import Plans from "@/pages/platform/Plans";
import Modules from "@/pages/platform/Modules";
import Announcements from "@/pages/platform/Announcements";
import Admins from "@/pages/platform/Admins";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import "@/App.css";

const Protected = ({ children }) => {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading your books…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <AppShell>{children}</AppShell>;
};

/** Where an already-signed-in visitor to /login should go. Honours the ?next=
 *  the 401 interceptor sets, so a dropped session resumes its page. This guard
 *  fires the moment auth state flips, so it — not the Login component — decides
 *  the destination. */
const useNextPath = (fallback, prefix = "/") => {
  const [params] = useSearchParams();
  const next = params.get("next");
  return next && next.startsWith(prefix) ? next : fallback;
};

const LoginRoute = () => {
  const { loading, user } = useAuth();
  const target = useNextPath("/home");
  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-50 text-sm text-slate-500">Loading…</div>;
  if (user) return <Navigate to={target} replace />;
  return <Login />;
};

/** SyncBooks — everything a business user sees. */
const TenantApp = () => (
  <AuthProvider>
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<Protected><Home /></Protected>} />
      <Route path="/connect" element={<Protected><TallyConnect /></Protected>} />
      <Route path="/items" element={<Protected><Items /></Protected>} />
      <Route path="/items/:guid" element={<Protected><ItemView /></Protected>} />
      <Route path="/customers" element={<Protected><Parties kind="customer" /></Protected>} />
      <Route path="/customers/:guid" element={<Protected><Parties kind="customer" /></Protected>} />
      <Route path="/vendors" element={<Protected><Parties kind="vendor" /></Protected>} />
      <Route path="/vendors/:guid" element={<Protected><Parties kind="vendor" /></Protected>} />
      {["invoices", "receipts", "credit-notes", "bills", "payments", "debit-notes", "journals"].map((kind) => (
        <Route key={kind} path={`/${kind}`} element={<Protected><Vouchers kind={kind} key={kind} /></Protected>} />
      ))}
      <Route path="/vouchers/:guid" element={<Protected><VoucherView /></Protected>} />
      <Route path="/accounts" element={<Protected><ChartOfAccounts /></Protected>} />
      <Route path="/accounts/:guid" element={<Protected><LedgerPage /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/reports/:key" element={<Protected><ReportView /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  </AuthProvider>
);

const PlatformProtected = ({ children }) => {
  const { loading, admin } = usePlatformAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#14121f]">
        <div className="text-sm text-slate-400">Loading console…</div>
      </div>
    );
  if (!admin) return <Navigate to="/platform/login" replace />;
  return <PlatformShell>{children}</PlatformShell>;
};

const PlatformLoginRoute = () => {
  const { loading, admin } = usePlatformAuth();
  // Console paths only: a stale next must never cross the two sessions.
  const target = useNextPath("/platform", "/platform");
  if (loading) return <div className="min-h-screen grid place-items-center bg-[#14121f] text-sm text-slate-400">Loading…</div>;
  if (admin) return <Navigate to={target} replace />;
  return <PlatformLogin />;
};

/**
 * Superadmin console — its own provider so a tenant session and a platform
 * session never observe each other's state.
 */
const PlatformApp = () => (
  <PlatformAuthProvider>
    <Routes>
      <Route path="login" element={<PlatformLoginRoute />} />
      <Route index element={<PlatformProtected><PlatformDashboard /></PlatformProtected>} />
      <Route path="organizations" element={<PlatformProtected><Organizations /></PlatformProtected>} />
      <Route path="organizations/:id" element={<PlatformProtected><OrganizationDetail /></PlatformProtected>} />
      <Route path="plans" element={<PlatformProtected><Plans /></PlatformProtected>} />
      <Route path="modules" element={<PlatformProtected><Modules /></PlatformProtected>} />
      <Route path="announcements" element={<PlatformProtected><Announcements /></PlatformProtected>} />
      <Route path="admins" element={<PlatformProtected><Admins /></PlatformProtected>} />
      <Route path="audit" element={<PlatformProtected><PlatformAudit /></PlatformProtected>} />
      <Route path="*" element={<Navigate to="/platform" replace />} />
    </Routes>
  </PlatformAuthProvider>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/platform/*" element={<PlatformApp />} />
        <Route path="/*" element={<TenantApp />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
